import { useMemo } from 'react';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { OrdersTable } from './components/orders-table/orders-table.component';
import { useOrdersList } from './hooks/use-orders-list.hook';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { RestError } from '@azure/core-http';
import { ProjectPathParams, Paths } from '../../../app/router/app.router';
import { OrdersListActionsObserver } from './order-list-actions-observer';
import { makeOrdersListPageHeaderProps } from './logic/orders-list-props.map';
import { OrdersListActions } from './actions/orders-list.actions';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { Container } from '@mui/material';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';

export const OrdersListPage = ({ params }: { params: ProjectPathParams<Paths.orders> }) => {
  const {
    project,
    orders,
    infiniteScroll,
    formData,
    queryData,
    setFormData,
    actions,
    hubFilter,
    showHubNameInTable,
    bookingFlowType
  } = useOrdersList(params.projectID);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );

  const error = orders.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  // Wait for the project to load before deciding which booking flow version of the
  // page header (and its flow-dependent status options) to render, to avoid jumpiness.
  if (project.isLoading) {
    return <LoadingIndicator />;
  }

  const headerProps = makeOrdersListPageHeaderProps(
    params.projectID,
    orders.data?.pages[0]?.totalRecords || 0,
    formData,
    setFormData,
    () => actions.setAction(OrdersListActions.BOOK_ORDER),
    actions.setAction,
    hubFilter,
    bookingFlowType
  );

  return (
    <Container>
      {project.data && <WindowTitle detail={project.data.name} pageTitle="Orders" />}
      <ListPageHeader {...headerProps} />
      <OrdersTable
        params={params}
        pages={orders.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={orders.isLoading}
        isPreviousData={orders.isPreviousData}
        hasError={orders.isError}
        showHubName={showHubNameInTable}
      />
      <OrdersListActionsObserver actions={actions} params={params} filters={queryData} />
    </Container>
  );
};
